<div id="print-section">
  <!-- ── Title row ─────────────────────────────────────────────────────── -->
  <div class="p-header">
    <div class="p-header__left">
      <span class="p-wo-number">Work Order — {{ workOrderId }}</span>
      <span class="p-badge p-badge--info">{{ workOrderStatus }}</span>
      @if (workOrder?.reeferContainerCondition === 'BOX_OK') {
        <span class="p-badge p-badge--success">BOX OK</span>
      }
      @if (workOrder?.reeferContainerCondition === 'MAC_OK') {
        <span class="p-badge p-badge--success">MAC OK</span>
      }
    </div>
    <div class="p-header__right">Generated: {{ today | date: 'dd MMM yyyy, HH:mm' }}</div>
  </div>

  <!-- ── Section 1: Work Order Information ────────────────────────────── -->
  <div class="p-card">
    <div class="p-card__title">Work Order Information</div>
    <div class="p-fields-grid">
      <div class="p-field">
        <div class="p-field__value">{{ workOrder?.maintenanceShopCode ?? '-' }}</div>
        <div class="p-field__label">Shop Code</div>
      </div>
      <div class="p-field">
        <div class="p-field__value">{{ workOrder?.vendorsReference ?? '-' }}</div>
        <div class="p-field__label">Vendor Ref No.</div>
      </div>

      <div class="p-field">
        <div class="p-field__value">{{ workOrder?.equipmentNumber ?? '-' }}</div>
        <div class="p-field__label">Equipment Number</div>
      </div>

      <div class="p-field">
        <div class="p-field__value">{{ workOrder?.maintenanceRepairModeCode ?? '-' }}</div>
        <div class="p-field__label">Mode</div>
      </div>

      <div class="p-field">
        <div class="p-field__value">{{ workOrder?.isoCurrencyCode ?? '-' }}</div>
        <div class="p-field__label">Currency</div>
      </div>

      <div class="p-field">
        <div class="p-field__value">{{ workOrder?.maintenanceOrderCauseDisplayText ?? '-' }}</div>
        <div class="p-field__label">Cause</div>
      </div>

      @if (workOrder?.purchaseOrderNumber) {
        <div class="p-field">
          <div class="p-field__value">{{ workOrder?.purchaseOrderNumber }}</div>
          <div class="p-field__label">PO Number</div>
        </div>
      }

      <div class="p-field">
        <div class="p-field__value">{{ workOrder?.previousLocationCode ?? '-' }}</div>
        <div class="p-field__label">Previous Repair Location</div>
      </div>

      <div class="p-field">
        <div class="p-field__value">{{ gensetHours ?? '-' }}</div>
        <div class="p-field__label">Gen Hours</div>
      </div>

      @if (workOrder?.maintenanceOrderCause === 'CONFIRMED_3RD_PARTY') {
        <div class="p-field">
          <div class="p-field__value">{{ workOrder?.damagedAt ?? '-' }}</div>
          <div class="p-field__label">Third Party Port</div>
        </div>
      }

      <div class="p-field">
        <div class="p-field__value">{{ workOrder?.createdAt | date: 'dd MMM yyyy' }}</div>
        <div class="p-field__label">Created On</div>
      </div>
    </div>
  </div>

  <!-- ── Section 2: Equipment Details ──────────────────────────────────── -->
  @if (equipment) {
    <div class="p-card">
      <div class="p-card__title">Equipment Details</div>
      <div class="p-fields-grid">
        <div class="p-field">
          <div class="p-field__value">{{ equipment.equipmentTypeCode ?? '-' }}/{{ equipment.equipmentTypeParentCode ?? '-' }}</div>
          <div class="p-field__label">Type</div>
        </div>
        <div class="p-field">
          <div class="p-field__value">{{ (equipment.infleetDate | date) ?? '-' }}</div>
          <div class="p-field__label">In Service</div>
        </div>
        <div class="p-field">
          <div class="p-field__value">{{ (equipment.manufacturingDateTime | date: 'yyyy') ?? '-' }}</div>
          <div class="p-field__label">Production Year</div>
        </div>
        <div class="p-field">
          <div class="p-field__value">{{ equipment.equipmentProfile ?? '-' }}</div>
          <div class="p-field__label">Profile</div>
        </div>
        <div class="p-field">
          <div class="p-field__value">{{ equipment.reeferUnitManufacturerName ?? '-' }}/{{ equipment.reeferUnitModel ?? '-' }}</div>
          <div class="p-field__label">Reefer Make/Model</div>
        </div>
        <div class="p-field">
          <div class="p-field__value">{{ getEquipmentSize() }}</div>
          <div class="p-field__label">Size</div>
        </div>

        <div class="p-field">
          <div class="p-field__value">{{ (equipment.ptiStatus?.date | date) ?? 'Not Found' }}</div>
          <div class="p-field__label">PTI Date</div>
        </div>
        <div class="p-field">
          <div class="p-field__value">{{ mapDamageCode(equipment.damageCode) }}</div>
          <div class="p-field__label">Damage</div>
        </div>
        <div class="p-field">
          <div class="p-field__value">{{ equipment.gradeCode ?? '-' }}</div>
          <div class="p-field__label">Grade Code</div>
        </div>
        <div class="p-field">
          <div class="p-field__value">{{ equipment.constructionMaterialCode ?? '-' }}</div>
          <div class="p-field__label">Material</div>
        </div>
        <div class="p-field">
          <div class="p-field__value">{{ workOrder?.locationCode ?? '-' }}</div>
          <div class="p-field__label">Shop Location</div>
        </div>
        <div class="p-field">
          <div class="p-field__value">{{ equipment.currentMove ?? '-' }}</div>
          <div class="p-field__label">Curr Move</div>
        </div>
        <div class="p-field">
          <div class="p-field__value">
            {{ equipment.isTaggedForSelling === true ? 'Y' : equipment.isTaggedForSelling === false ? 'N' : '-' }}
          </div>
          <div class="p-field__label">For Sale</div>
        </div>
        <div class="p-field">
          <div class="p-field__value">{{ equipment.ptiStatus?.description ?? 'Not Found' }}</div>
          <div class="p-field__label">Container/PTI Status</div>
        </div>

        <div class="p-field">
          <div class="p-field__value">{{ equipment.ownershipTypeCode ?? '-' }}</div>
          <div class="p-field__label">Owner</div>
        </div>

        <div class="p-field">
          <div class="p-field__value">{{ equipment.leasingCompanyCode ?? '-' }}</div>
          <div class="p-field__label">Leasing Company</div>
        </div>

        <div class="p-field">
          <div class="p-field__value">{{ equipment.leasingContractNumber ?? '-' }}</div>
          <div class="p-field__label">Leasing Contract No</div>
        </div>

        <div class="p-field">
          <div class="p-field__value">
            {{ equipment.isTaggedForRedelivery === true ? 'Y' : equipment.isTaggedForRedelivery === false ? 'N' : '-' }}
          </div>
          <div class="p-field__label">On Global Hunt</div>
        </div>

        <div class="p-field">
          <div class="p-field__value">{{ (equipment.containerGateInDate | date) ?? '-' }}</div>
          <div class="p-field__label">Gate In Date</div>
        </div>
        <div class="p-field">
          <div class="p-field__value">{{ equipment.presentLocation ?? '-' }}</div>
          <div class="p-field__label">Present Location</div>
        </div>
        <div class="p-field">
          <div class="p-field__value">
            {{ equipment.isAtOffHireLocation === true ? 'Y' : equipment.isAtOffHireLocation === false ? 'N' : '-' }}
          </div>
          <div class="p-field__label">At Off-Hire Loc</div>
        </div>
        <div class="p-field">
          <div class="p-field__value">
            {{ equipment.isContainerEmpty === true ? 'Y' : equipment.isContainerEmpty === false ? 'N' : '-' }}
          </div>
          <div class="p-field__label">Empty</div>
        </div>
      </div>
    </div>
  }

  <!-- ── Section 3: Repair Line Items ──────────────────────────────────── -->
  @if (workOrder?.maintenanceOrderLines?.length) {
    <div class="p-card">
      <div class="p-card__title">Repair Line Items</div>
      <table class="p-table">
        <thead>
          <tr>
            <th class="col-num">#</th>
            <th>Damage Code</th>
            <th>Repair Code</th>
            <th>Description</th>
            <th>Rep. Loc</th>
            <th>TPI</th>
            <th class="text-right">Pcs</th>
            <th class="text-right">Man Hrs/Pc</th>
            <th class="text-right">Mat. Cost/Pc</th>
            <th class="text-right">Item Total</th>
          </tr>
        </thead>
        <tbody>
          @for (item of workOrder?.maintenanceOrderLines; track $index) {
            <tr>
              <td class="col-num">{{ $index + 1 }}</td>
              <td>{{ item.equipmentDamageCode }}</td>
              <td>{{ item.maintenanceTypeCode }}</td>
              <td>{{ item.maintenanceTypeName ?? '-' }}</td>
              <td>{{ item.maintenanceRepairLocationCode }}</td>
              <td>{{ getTPICode(item.thirdPartyIndicator) }}</td>
              <td class="text-right">{{ item.repairQuantity }}</td>
              <td class="text-right">{{ item.manHoursPerPiece }}</td>
              <td class="text-right">{{ item.materialCostPerPiece | number: '1.2-2' }}</td>
              <td class="text-right">{{ getItemTotal(item) | number: '1.2-2' }}</td>
            </tr>

            @if (item.maintenanceOrderParts.length) {
              <tr class="p-parts-row">
                <td></td>
                <td colspan="9">
                  <table class="p-sub-table">
                    <thead>
                      <tr>
                        <th>Part No.</th>
                        <th>Part Name</th>
                        <th class="text-right">Qty</th>
                        <th class="text-right">Unit Price</th>
                        <th>Old Serial No.</th>
                        <th>New Serial No.</th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (part of item.maintenanceOrderParts; track $index) {
                        <tr>
                          <td>{{ part.partNumber }}</td>
                          <td>{{ part.partName ?? '-' }}</td>
                          <td class="text-right">{{ part.unitQuantity }}</td>
                          <td class="text-right">{{ part.unitPrice | number: '1.2-2' }}</td>
                          <td>{{ part.currentSerialNumbers?.join(', ') ?? '-' }}</td>
                          <td>{{ part.newSerialNumbers?.join(', ') ?? '-' }}</td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </td>
              </tr>
            }
          }
        </tbody>
      </table>
    </div>
  }

  <!-- ── Section 4: Cost Summary ────────────────────────────────────────── -->
  @if (totalCost) {
    <div class="p-card">
      <div class="p-card__title">Cost Summary</div>
      <div class="p-manhours">
        Total Man Hours: <strong>{{ totalManHoursSummary }}</strong>
      </div>
      <table class="p-cost-table">
        <thead>
          <tr>
            <th>Description</th>
            <th class="text-right">Shop Cost (USD)</th>
            <th class="text-right">Shop Cost ({{ totalCost.shopCurrencyCode }})</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Total Labor Cost</td>
            <td class="text-right">
              {{ totalCost.labourHourDistribution.totalLabourHoursPriceBeforeTax * totalCost.shopExchangeRateToUsd | number: '1.2-2' }}
            </td>
            <td class="text-right">{{ totalCost.labourHourDistribution.totalLabourHoursPriceBeforeTax | number: '1.2-2' }}</td>
          </tr>
          <tr>
            <td>Total Material Cost</td>
            <td class="text-right">{{ totalCost.totalMaterialCostWithoutTax * totalCost.shopExchangeRateToUsd | number: '1.2-2' }}</td>
            <td class="text-right">{{ totalCost.totalMaterialCostWithoutTax | number: '1.2-2' }}</td>
          </tr>
          <tr class="p-separator-row">
            <td>Total Wear and Tear</td>
            <td class="text-right">{{ getWearAndTearCostUsd() | number: '1.2-2' }}</td>
            <td class="text-right">{{ getWearAndTearCostLocal() | number: '1.2-2' }}</td>
          </tr>
          <tr>
            <td>Total Third Party</td>
            <td class="text-right">{{ getThirdPartyCostUsd() | number: '1.2-2' }}</td>
            <td class="text-right">{{ getThirdPartyCostLocal() | number: '1.2-2' }}</td>
          </tr>
          <tr class="p-separator-row">
            <td>Total Cost excl. Tax/VAT</td>
            <td class="text-right">{{ totalCost.totalOrderCostWithoutTax * totalCost.shopExchangeRateToUsd | number: '1.2-2' }}</td>
            <td class="text-right">{{ totalCost.totalOrderCostWithoutTax | number: '1.2-2' }}</td>
          </tr>
          <tr class="p-total-row">
            <td><strong>Total Cost incl. Tax/VAT</strong></td>
            <td class="text-right">
              <strong>{{ totalCost.totalOrderCostWithTax * totalCost.shopExchangeRateToUsd | number: '1.2-2' }}</strong>
            </td>
            <td class="text-right">
              <strong>{{ totalCost.totalOrderCostWithTax | number: '1.2-2' }}</strong>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  }

  <!-- ── Section 5: Remarks (optional) ─────────────────────────────────── -->
  @if (includeRemarks && workOrder?.maintenanceOrderRemarks?.length) {
    <div class="p-card">
      <div class="p-card__title">Remarks</div>
      <div class="p-remarks">
        @for (remark of workOrder?.maintenanceOrderRemarks; track $index) {
          <div class="p-remark">
            <div class="p-remark__meta">
              <span [class]="'p-remark__badge p-remark__badge--' + getRemarkLabel(remark).toLowerCase()">
                {{ getRemarkLabel(remark) }}
              </span>
              @if (remark.createUserId) {
                <span class="p-remark__user">{{ remark.createUserId }}</span>
              }
              @if (remark.createTimestamp) {
                <span class="p-remark__time">{{ remark.createTimestamp | date: 'yyyy-MM-dd HH:mm' }}</span>
              }
            </div>
            <div class="p-remark__text">{{ remark.maintenanceOrderRemarkText }}</div>
          </div>
        }
      </div>
    </div>
  }

  <!-- ── Footer ─────────────────────────────────────────────────────────── -->
  <div class="p-footer">
    <span>Maersk A/S — CONFIDENTIAL</span>
    <span>{{ today | date: 'dd MMM yyyy, HH:mm' }}</span>
  </div>
</div>
